import React from 'react';
import {
  Box, Container, Typography, Paper, Grid, Chip, Card, CardContent,
} from '@mui/material';
import {
  Storage, Analytics, Language, CalendarToday, Article, TrendingUp,
  SentimentSatisfied, Public, School, Flag, AccountBalance,
} from '@mui/icons-material';
import { colors } from '../styles/theme';

const STATS = [
  { icon: <Article />, value: '197,479', label: '条新闻语料' },
  { icon: <Language />, value: '73', label: '个中文热词' },
  { icon: <CalendarToday />, value: '2014-2025', label: '时间跨度' },
  { icon: <Public />, value: '38家', label: '国际主流媒体' },
  { icon: <Flag />, value: '5', label: '个英语国家' },
];

const FEATURES = [
  {
    icon: <Analytics sx={{ fontSize: 32 }} />,
    title: '多维分析',
    desc: '从情感倾向、报道主题、媒体来源等多维度分析每个热词的国际传播态势',
  },
  {
    icon: <TrendingUp sx={{ fontSize: 32 }} />,
    title: '趋势追踪',
    desc: '可视化展示热词在不同年份的报道量变化与情感走向',
  },
  {
    icon: <SentimentSatisfied sx={{ fontSize: 32 }} />,
    title: '情感分析',
    desc: '基于自然语言处理技术，对每条语料进行情感极性标注（积极/消极/中性）',
  },
  {
    icon: <Storage sx={{ fontSize: 32 }} />,
    title: '语料检索',
    desc: '支持按热词、年份、情感、主题等条件精细筛选，快速定位目标语料',
  },
];

const TIMELINE = [
  { year: '2014', words: '打虎拍蝇、断崖式、你懂的...' },
  { year: '2015', words: '互联网+、众创空间、获得感...' },
  { year: '2016', words: '供给侧、工匠精神、洪荒之力...' },
  { year: '2017', words: '雄安新区、共有产权房、租购同权...' },
  { year: '2018', words: '命运共同体、锦鲤、官宣...' },
  { year: '2019', words: '硬核、融梗、区块链...' },
  { year: '2020', words: '逆行者、后浪、内卷...' },
  { year: '2021', words: '共同富裕、双减、躺平...' },
  { year: '2022', words: '中国式现代化、烟火气、栓Q...' },
  { year: '2023', words: '新质生产力、村超、特种兵式旅游...' },
  { year: '2024', words: '新质生产力、未来产业、数智化...' },
  { year: '2025', words: '持续更新中...' },
];

const COUNTRIES = ['美国', '英国', '加拿大', '澳大利亚', '南非'];

function About() {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background.default, pb: 8 }}>
      {/* Hero */}
      <Box sx={{
        background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 50%, ${colors.primary.light} 100%)`,
        color: 'white', py: { xs: 5, md: 7 },
      }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 680 }}>
            <Chip label="About" size="small" sx={{
              backgroundColor: 'rgba(255,255,255,0.2)', color: 'white',
              fontWeight: 600, mb: 2, letterSpacing: 1,
            }} />
            <Typography variant="h2" sx={{
              fontFamily: '"Noto Serif SC", serif', fontWeight: 700, mb: 2,
              fontSize: { xs: '1.8rem', md: '2.5rem' }, lineHeight: 1.3,
            }}>
              关于本项目
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.9, fontSize: '1.05rem' }}>
              Chinese Buzzwords in International News Database
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Stats */}
        <Grid container spacing={2} sx={{ mt: -4, mb: 5, position: 'relative', zIndex: 10 }}>
          {STATS.map((s) => (
            <Grid item xs={6} md={2.4} key={s.label}>
              <Paper sx={{
                p: 3, textAlign: 'center', borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              }}>
                <Box sx={{ color: colors.primary.main, mb: 1 }}>{s.icon}</Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: colors.primary.dark }}>
                  {s.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* 政策背景 */}
        <Paper sx={{ p: { xs: 3, md: 5 }, mb: 5, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <AccountBalance sx={{ color: colors.primary.main, fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: '"Noto Serif SC", serif' }}>
              政策背景
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ lineHeight: 2.2, color: colors.text.secondary, mb: 2, textIndent: '2em' }}>
            党的二十大报告中指出，&ldquo;增强中华文明传播力影响力。坚守中华文化立场，提炼展示中华文明的精神标识和文化精髓，加快构建中国话语和中国叙事体系，讲好中国故事、传播好中国声音，展现可信、可爱、可敬的中国形象。&rdquo;这一重要论述为国家语言能力和国际传播能力建设提供了战略指引，并在数字中文建设背景下，进一步凸显了语言资源在国家话语体系建设中的基础性支撑作用。
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 2.2, color: colors.text.secondary, textIndent: '2em' }}>
            自 2006 年开始，由国家语言资源监测与研究中心、商务印书馆等联手打造的系列文化活动&ldquo;汉语盘点&rdquo;在每年年末发布年度国内字、国内词、国际字、国际词，以及十大流行语、十大新词语、十大网络用语，旨在&ldquo;用一个字、一个词描述当年的中国与世界&rdquo;，鼓励全民用语言记录生活，描述中国视野下的社会变迁和世界万象。近年来，不少中文热词&ldquo;扬帆出海&rdquo;，得到了海外媒体和网民的关注，它们进一步成为观察中国概念在国际新闻话语中的呈现方式、传播路径及意义建构的重要载体。
          </Typography>
        </Paper>

        {/* 课题来源 */}
        <Paper sx={{ p: { xs: 3, md: 5 }, mb: 5, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <School sx={{ color: colors.primary.main, fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: '"Noto Serif SC", serif' }}>
              课题来源
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ lineHeight: 2.2, color: colors.text.secondary, mb: 2, textIndent: '2em' }}>
            本检索系统为国家语委 2022 年度中青班项目&ldquo;中文热词在海外媒体中的传播现状及效果研究&rdquo;（YB145-49）的研究成果。该课题重点聚焦中文热词在海外媒体中的传播现状，基于历年来中文热词在海外新闻媒体中的翻译及使用情况，研究哪些中文热词得到了海外新闻媒体的更多关注，以及它们在国际媒体中是如何被报道和解读的，由此为未来中文热词提高国际影响力提供参考。
          </Typography>
        </Paper>

        {/* 数据与方法 */}
        <Paper sx={{ p: { xs: 3, md: 5 }, mb: 5, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Storage sx={{ color: colors.primary.main, fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: '"Noto Serif SC", serif' }}>
              数据与方法
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ lineHeight: 2.2, color: colors.text.secondary, mb: 2, textIndent: '2em' }}>
            在此背景下，本检索系统基于 LexisNexis Academic 数据库，收集了 2014&ndash;2025 年&ldquo;汉语盘点&rdquo;发布的十大流行语、十大新词语、十大网络词语中属于中文原创概念的热词，在美国、英国、加拿大、澳大利亚、南非五国共 38 家媒体中的新闻语料，最终构建了包含 73 个中文热词、197,479 条英文语料的分析数据库。
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 2.2, color: colors.text.secondary, mb: 3, textIndent: '2em' }}>
            每条语料均标注了情感极性、报道主题和媒体来源等信息，并在统一的数据标注框架下实现多维度结构化处理，确保数据的系统性与可比性。系统同时将上述研究成果以可视化、可交互的方式呈现，支持按热词、年份、情感、主题等多维度筛选和分析。
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {COUNTRIES.map((c) => (
              <Chip key={c} icon={<Flag sx={{ fontSize: 16 }} />} label={c} size="small"
                sx={{ backgroundColor: colors.accent.cool, fontWeight: 500 }} />
            ))}
          </Box>
        </Paper>

        {/* 研究意义 */}
        <Paper sx={{
          p: { xs: 3, md: 5 }, mb: 5, borderRadius: 3,
          border: `2px solid ${colors.primary.light}`,
          background: `linear-gradient(135deg, ${colors.accent.highlight} 0%, #FFFFFF 100%)`,
        }}>
          <Typography variant="body1" sx={{
            lineHeight: 2.2, color: colors.text.secondary, textIndent: '2em',
            fontFamily: '"Noto Serif SC", serif', fontSize: '1.05rem',
          }}>
            本系统能够为跨文化传播、翻译研究、舆情分析等领域的学者提供数据支撑，并进一步服务于中国话语体系国际传播能力的提升。
          </Typography>
        </Paper>

        {/* 系统功能 */}
        <Typography variant="h5" sx={{
          fontWeight: 700, fontFamily: '"Noto Serif SC", serif', mb: 3, textAlign: 'center',
        }}>
          系统功能
        </Typography>
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {FEATURES.map((f) => (
            <Grid item xs={12} sm={6} key={f.title}>
              <Card sx={{
                height: '100%', borderRadius: 3,
                border: `1px solid ${colors.accent.cool}`,
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderColor: colors.primary.light },
                transition: 'all 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ color: colors.primary.main, mb: 2 }}>{f.icon}</Box>
                  <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '1.05rem' }}>{f.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{f.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 引用说明 */}
        <Paper sx={{
          p: 3, borderRadius: 3, border: `1px solid ${colors.secondary.light}`,
          backgroundColor: colors.accent.highlight,
        }}>
          <Typography sx={{ fontWeight: 600, mb: 1, color: colors.primary.dark }}>
            数据引用
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            如需引用本系统的数据或分析结果，请注明：&ldquo;中文热词国际新闻报道检索系统&rdquo;（Chinese Buzzwords in International News Database），
            数据来源：LexisNexis Academic，时间范围：2014&ndash;2025。
            课题编号：YB145-49。如有学术合作或数据使用需求，欢迎联系研究团队。
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default About;
